import Yup from 'utils/validation/yup-extensions';
import { getSubUserTypes } from './_api';

export const CSS_CLASSES = {
	INPUT: 'user-type__input',
};

/**
 * Form property keys.
 */
export const FORM_KEYS = {
	USER_TYPE: 'USER_TYPE',
	USER_TYPE_OTHER: 'USER_TYPE_OTHER',
	USER_TYPE_RESEARCHER_INFO: 'USER_TYPE_RESEARCHER_INFO',
	USER_TYPE_REGULATORY_USER_AGENCY: 'USER_TYPE_REGULATORY_USER_AGENCY',
	USER_TYPE_REGULATORY_USER_LOCATION: 'USER_TYPE_REGULATORY_USER_LOCATION',
	USER_TYPE_STUDENT_COLLEGE: 'USER_TYPE_STUDENT_COLLEGE',
	USER_TYPE_VENDOR_ACCESS: 'USER_TYPE_VENDOR_ACCESS',
};

/**
 * Default Formik form initial value object.
 * @type {object}
 */
export const FORM_INITIAL_VALUES = Object.keys(FORM_KEYS).reduce((acc, key) => {
	acc[FORM_KEYS[key]] = '';
	return acc;
}, {});

/**
 * Keys of user type subset of interest.
 */
export const USER_TYPES = {
	OTHER: 'other',
	REGULATORY_USER: 'regulatory user',
	RESEARCHER: 'researcher',
	STUDENT: 'student',
	VENDOR: 'vendor',
};

/**
 * Yup helper method performing case-insensitive equality check between @param actual and @param expected.
 * @function
 * @param {string} actual parameter to be checked.
 * @param {string} expected parameter to be checked against.
 * @returns {boolean} equality indication.
 */
const validationMatcher = (actual, expected) =>
	String(actual).toLocaleLowerCase() === String(expected).toLocaleLowerCase();

const VALIDATION_MESSAGE = 'Field is required.';

/**
 * Default Formik form Yup validation schema logic.
 * @type {object}
 */
export const FORM_VALIDATION_SCHEMA_OBJECT = {
	[FORM_KEYS.USER_TYPE]: Yup.string().required(VALIDATION_MESSAGE),
	[FORM_KEYS.USER_TYPE_OTHER]: Yup.string().when(FORM_KEYS.USER_TYPE, {
		is: val => validationMatcher(val, USER_TYPES.OTHER),
		then: Yup.string().required(VALIDATION_MESSAGE),
		otherwise: Yup.string(),
	}),
	[FORM_KEYS.USER_TYPE_RESEARCHER_INFO]: Yup.string().when(
		FORM_KEYS.USER_TYPE,
		{
			is: val => validationMatcher(val, USER_TYPES.RESEARCHER),
			then: Yup.string().required(VALIDATION_MESSAGE),
			otherwise: Yup.string(),
		}
	),
	[FORM_KEYS.USER_TYPE_REGULATORY_USER_AGENCY]: Yup.string().when(
		FORM_KEYS.USER_TYPE,
		{
			is: val => validationMatcher(val, USER_TYPES.REGULATORY_USER),
			then: Yup.string().required(VALIDATION_MESSAGE),
			otherwise: Yup.string(),
		}
	),
	[FORM_KEYS.USER_TYPE_REGULATORY_USER_LOCATION]: Yup.string().test(
		'isLocationsRequired',
		VALIDATION_MESSAGE,
		async (value, context) => {
			let hasLocations = false;
			const userType = context?.parent?.[FORM_KEYS.USER_TYPE];
			const agency =
				context?.parent?.[FORM_KEYS.USER_TYPE_REGULATORY_USER_AGENCY];
			if (
				userType &&
				validationMatcher(userType, USER_TYPES.REGULATORY_USER) &&
				agency
			) {
				const agencies = await getSubUserTypes(14);
				const objAgency = agencies.find(el => el.value === agency);
				if (objAgency && objAgency.key) {
					hasLocations =
						(await getSubUserTypes(objAgency.key)).length > 0;
				}
			}
			return hasLocations ? (value ? true : false) : true;
		}
	),
	[FORM_KEYS.USER_TYPE_STUDENT_COLLEGE]: Yup.string().when(
		FORM_KEYS.USER_TYPE,
		{
			is: val => validationMatcher(val, USER_TYPES.STUDENT),
			then: Yup.string().required(VALIDATION_MESSAGE),
			otherwise: Yup.string(),
		}
	),
};

/**
 * Default Formik form Yup validation schema object.
 * @type {object}
 */
export const FORM_VALIDATION_SCHEMA = Yup.object(FORM_VALIDATION_SCHEMA_OBJECT);